import { useEffect, useRef, useState } from "react";
import { hhmm } from "../../../../shared/day";
import { noteId, postNote, snapJpeg } from "../../lib/feedback";
import type { Tournament } from "../../lib/useTournament";

/** How long the box waits for the wall before it says there was no answer. */
const WALL_WAIT_MS = 8000;

/**
 * What the owner's phone is showing, for the note: the tab, and the title of any fold that is open
 * and any question that is asking.
 */
function screenNow(tab: string): string {
  const folds = [...document.querySelectorAll(".ad-fold.open .ad-fold-title")].map((el) => el.textContent?.trim()).filter(Boolean);
  const asking = [...document.querySelectorAll(".ad-confirm p")].map((el) => el.textContent?.trim()).filter(Boolean);
  return [`${tab} tab`, folds.length ? `open: ${folds.join(", ")}` : null, asking.length ? `asking: ${asking.join(" / ")}` : null].filter(Boolean).join(", ");
}

/**
 * The owner's Note key (v26.10.027): a temporary test aid, to be removed or changed by Sunday 18
 * October. A tap takes a picture of this screen as it is, asks the wall for its own, then opens a
 * box for a note; Send keeps it on the server for Claude with what every screen was doing.
 */
export function NoteKey({ t, tab, say }: { t: Tournament; tab: string; say: (text: string) => void }) {
  const [open, setOpen] = useState(false);
  // a ref, not state: the key must look as it did when tapped in the picture of the screen
  const busy = useRef(false);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [waited, setWaited] = useState(false);
  const note = useRef<{ rid: string; at: number; screen: string; phone: Blob | null } | null>(null);
  const box = useRef<HTMLTextAreaElement | null>(null);
  const mock = t.who.mock;

  useEffect(() => {
    if (!open) return;
    box.current?.focus();
    const id = setTimeout(() => setWaited(true), WALL_WAIT_MS);
    return () => clearTimeout(id);
  }, [open]);

  async function start() {
    if (busy.current || open) return;
    const rid = noteId();
    const screen = screenNow(tab);
    if (!mock) t.noteAskWall(rid);
    busy.current = true;
    // the picture first, before the box is on the screen
    const phone = await snapJpeg(document.body, 400_000, true);
    note.current = { rid, at: t.now(), screen, phone };
    busy.current = false;
    setError(null);
    setWaited(false);
    setOpen(true);
  }

  async function send() {
    const n = note.current;
    if (!n || !text.trim() || sending) return;
    if (mock) {
      setText("");
      setOpen(false);
      say("Saved (practice screen, not sent)");
      return;
    }
    setSending(true);
    const id = await postNote(t.who.tournament, t.keeper.admin()?.session ?? null, { text: text.trim(), rid: n.rid, screen: n.screen, phone: n.phone });
    setSending(false);
    if (id === null) {
      setError("Could not save that. Try again.");
      return;
    }
    setText("");
    setOpen(false);
    say(`Saved, note ${id}`);
  }

  const n = note.current;
  const wall = n && t.noteWall?.rid === n.rid ? t.noteWall.text : null;
  const wallWords = mock ? "practice screen, no wall" : wall ?? (waited ? "no answer" : "asking");

  return (
    <>
      <button type="button" className="ad-small ad-notekey" onClick={() => void start()}>
        Note
      </button>
      {open && n ? (
        <div className="ad-notebox" role="dialog" aria-modal="true" aria-label="Note for Claude">
          <h2>Note for Claude</h2>
          <p className="ad-note">
            {`Carries: ${hhmm(n.at)}, ${n.screen}, ${n.phone ? "a picture of this screen" : "no picture of this screen"}. Wall: ${wallWords}.`}
          </p>
          <textarea ref={box} value={text} rows={6} placeholder="What did you see?" onChange={(e) => setText(e.target.value)} />
          {error ? <p className="ad-note ad-notebox-error" role="alert">{error}</p> : null}
          <div className="ad-row">
            <button type="button" className="btn" disabled={!text.trim() || sending} onClick={() => void send()}>
              {sending ? "Sending" : "Send"}
            </button>
            <button type="button" className="btn quiet" onClick={() => setOpen(false)}>
              Done
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
